import _xor from 'lodash/xor';
import _isEqual from 'lodash/isEqual';
import { FormsEntities } from '@bimser/common';
import { IDictionary } from '@bimser/core';
import { Map } from 'immutable';
import { JSX } from 'react';
import {
	IListItem,
	IPropertyField,
	ISelectableListItem,
	MultiLanguageText
} from '../../../entities/common';
import { IUserEvent } from '../../../entities/services';
import { geti18nValue } from '../../../helper';
import {
	IControlExecutedServerEvent,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../entities/EventArgs';
import { IListBoxBaseProperties } from '../entities/IListBoxBaseProperties';
import ListControl from './ListControl';
import cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';

export default class ListBoxBase extends ListControl {
	textSeparator: string;
	valueSeparator: string;
	showSearch: boolean;

	get selectedItems(): IListItem<MultiLanguageText>[] {
		const selectedValues: IListItem<MultiLanguageText>[] = [];
		Object.keys(this.__itemsCollection).forEach(value => {
			const item = this.__itemsCollection[value];
			if (item.selected) {
				selectedValues.push(item);
			}
		});
		return selectedValues;
	}
	set selectedItems(items: IListItem<MultiLanguageText>[]) {
		if (this.items && !_isEqual(this.selectedItems, items)) {
			this.__proxyChanging.items = this.items.map(i =>
				Object.assign({}, i, {
					selected: items.findIndex(foundItem => foundItem.value === i.value) > -1
				})
			);
		}
	}

	get value(): string {
		const selectedItems = this.selectedItems;
		return selectedItems ? selectedItems.map(i => i.value).join(this.valueSeparator) : null;
	}
	set value(value: string) {
		if (this.items && _xor(this.value, value?.split(this.valueSeparator)).length) {
			this.__proxyChanging.items = this.items.map(i =>
				Object.assign({}, i, { selected: value.indexOf(i.value) > -1 })
			);
		}
	}

	get text(): string {
		const selectedItems = this.selectedItems;
		return selectedItems
			? selectedItems
					.map(i => (i.text.getText ? i.text.getText() : ''))
					.join(this.textSeparator)
			: null;
	}
	set text(text: string) {
		if (this.items && _xor(this.text, text?.split(this.textSeparator)).length) {
			this.__proxyChanging.items = this.items.map(i => ({
				...i,
				selected: text.indexOf(i.text.getText()) > -1
			}));
		}
	}

	constructor() {
		super();

		this.textSeparator = ',';
		this.valueSeparator = ',';
		this.selectedItems = [];
		this.showSearch = false;
		this.__excludeChangingProperties = ['selectedItems', 'value', 'text'];

		this.subscribe(
			'system:PropertyChanging:items',
			(eventArgs: FormsEntities.IPropertyChangeEventArgs) =>
				new Promise<void>((resolve, reject) => {
					if (this.dataSourceType === 'dynamic' && !this.__dataSourceLoadedAtLeastOnce) {
						resolve();
					} else if (eventArgs.value) {
						const items: ISelectableListItem<IDictionary<string>>[] = eventArgs.value;
						const selectedItems = items.filter(i => i.selected);

						const newText = selectedItems
							.map(i => geti18nValue(i.text, this.__form.currentLanguage))
							.join(this.textSeparator);

						if (_xor(this.text, newText.split(this.textSeparator)).length) {
							const propertyChangeArgs = {
								name: this.name,
								newValue: eventArgs.value,
								oldValue: _get(this, eventArgs.dataField.join('.')),
								cancel: false
							};

							// TRIGGER PROPERTY CHANGING CLIENT EVENT
							this.emit<IPropertyChangingEventArgs<any>>(
								'event:OnPropertyChanging',
								propertyChangeArgs
							)
								.then(() => {
									if (!propertyChangeArgs.cancel) {
										const textArgument = {
											name: this.name,
											newValue: newText,
											oldValue: this.text,
											cancel: false
										};

										// TRIGGER TEXT CHANGING CLIENT EVENT
										this.emit<IPropertyChangingEventArgs<string>>(
											'event:OnTextChanging',
											textArgument
										)
											.then(() => {
												if (!textArgument.cancel) {
													// TRIGGER TEXT CHANGING SERVER EVENT
													this.emit<
														IUserEvent<
															IPropertyChangedEventArgs<string>
														>
													>('event:server:OnTextChanging', {
														argument: textArgument,
														controlId: this.name,
														name: 'TextChanging',
														sender: 'web'
													})
														.then(args => {
															const executedServerEvent: IControlExecutedServerEvent =
																args[0]?.executedServerEvent;
															if (
																!executedServerEvent ||
																!executedServerEvent?.parameters
																	.cancel
															) {
																const valueArgument = {
																	name: this.name,
																	newValue: newText,
																	oldValue: this.text,
																	cancel: false
																};

																// TRIGGER VALUE CHANGING CLIENT EVENT
																this.emit<
																	IPropertyChangingEventArgs<string>
																>(
																	'event:OnValueChanging',
																	valueArgument
																)
																	.then(() => {
																		if (!valueArgument.cancel) {
																			// TRIGGER VALUE CHANGING SERVER EVENT
																			this.emit<
																				IUserEvent<
																					IPropertyChangedEventArgs<string>
																				>
																			>(
																				'event:server:OnValueChanging',
																				{
																					argument:
																						valueArgument,
																					controlId:
																						this.name,
																					name: 'ValueChanging',
																					sender: 'web'
																				}
																			)
																				.then(args => {
																					const executedServerEvent: IControlExecutedServerEvent =
																						args[0]
																							?.executedServerEvent;
																					if (
																						!executedServerEvent ||
																						!executedServerEvent
																							?.parameters
																							.cancel
																					) {
																						const newSelectedItems: IListItem<MultiLanguageText>[] =
																							[];
																						const oldSelectedItems: IListItem<MultiLanguageText>[] =
																							[];

																						selectedItems.forEach(
																							selectedItem => {
																								const mlNewText =
																									new MultiLanguageText();
																								if (
																									selectedItem
																								) {
																									mlNewText.setData(
																										selectedItem.text
																									);
																								}
																								newSelectedItems.push(
																									{
																										...selectedItem,
																										text: mlNewText
																									}
																								);
																							}
																						);

																						this.selectedItems.forEach(
																							selectedItem => {
																								const mlOldText =
																									new MultiLanguageText();
																								if (
																									selectedItem
																								) {
																									mlOldText.setData(
																										selectedItem.text
																									);
																								}
																								oldSelectedItems.push(
																									{
																										...selectedItem,
																										text: mlOldText
																									}
																								);
																							}
																						);

																						const selectedItemMLArgument =
																							{
																								name: this
																									.name,
																								newValue:
																									newSelectedItems,
																								oldValue:
																									oldSelectedItems,
																								cancel: false
																							};

																						// TRIGGER SELECTED ITEM CHANGING CLIENT EVENT
																						this.emit<
																							IPropertyChangedEventArgs<
																								IListItem<MultiLanguageText>[]
																							>
																						>(
																							'event:OnSelectedItemsChanging',
																							selectedItemMLArgument
																						)
																							.then(
																								() => {
																									if (
																										!selectedItemMLArgument.cancel
																									) {
																										const selectedItemArgument =
																											{
																												name: this
																													.name,
																												newValue:
																													selectedItems,
																												oldValue:
																													this.selectedItems.map(
																														i => ({
																															...i,
																															text:
																																i
																																	.text
																																	?.getData &&
																																i.text?.getData()
																														})
																													),
																												cancel: false
																											};

																										// TRIGGER SELECTED ITEM CHANGING SERVER EVENT
																										this.emit<
																											IUserEvent<
																												IPropertyChangedEventArgs<
																													IListItem<
																														IDictionary<string>
																													>[]
																												>
																											>
																										>(
																											'event:server:OnSelectedItemsChanging',
																											{
																												argument:
																													selectedItemArgument,
																												controlId:
																													this
																														.name,
																												name: 'SelectedItemsChanging',
																												sender: 'web'
																											}
																										)
																											.then(
																												args => {
																													const executedServerEvent: IControlExecutedServerEvent =
																														args[0]
																															?.executedServerEvent;
																													if (
																														!executedServerEvent ||
																														!executedServerEvent
																															?.parameters
																															.cancel
																													) {
																														resolve();
																													} else {
																														reject();
																													}
																												}
																											)
																											.catch(
																												() => {
																													reject();
																												}
																											);
																									} else {
																										reject();
																									}
																								}
																							)
																							.catch(
																								() => {
																									reject();
																								}
																							);
																					} else {
																						reject();
																					}
																				})
																				.catch(() => {
																					reject();
																				});
																		} else {
																			reject();
																		}
																	})
																	.catch(() => {
																		reject();
																	});
															} else {
																reject();
															}
														})
														.catch(() => {
															reject();
														});
												} else {
													reject();
												}
											})
											.catch(() => {
												reject();
											});
									} else {
										reject();
									}
								})
								.catch(() => {
									reject();
								});
						} else {
							reject();
						}
					} else {
						reject();
					}
				})
		);
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'selectedItems.__array__.text'];
	}

	__deepClone(obj) {
		return cloneDeep(obj);
	}

	__compareItems(oldItems: any, newItems: any) {
		const oldItemsClone = this.__deepClone(oldItems).map(i => {
			i.text = i.text?.getData?.() || i.text;
			i.icon = i.icon || '';
			delete i.key;
			return i;
		});

		const newItemsClone = this.__deepClone(newItems).map(i => {
			i.text = i.text?.getData?.() || i.text;
			i.icon = i.icon || '';
			delete i.key;
			return i;
		});

		return {
			isEqual: _isEqual(oldItemsClone, newItemsClone),
			oldItemsClone,
			newItemsClone
		};
	}

	__loadProperties(data: IListBoxBaseProperties) {
		const oldSelectedItems = this.selectedItems;
		const oldValue = this.value;
		const oldText = this.text;

		super.__loadProperties(data);

		this.__proxyChanged.textSeparator = data.textSeparator;
		this.__proxyChanged.valueSeparator = data.valueSeparator;
		this.__proxyChanged.showSearch = data.showSearch;

		const compare = this.__compareItems(oldSelectedItems, this.selectedItems);

		if (!compare.isEqual && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: this.selectedItems,
				oldValue: oldSelectedItems
			};
			this.emit<IPropertyChangedEventArgs<IListItem<MultiLanguageText>[]>>(
				'event:OnSelectedItemsChanged',
				argument
			);

			this.emit<IUserEvent<IPropertyChangedEventArgs<IListItem<IDictionary<string>>[]>>>(
				'event:server:OnSelectedItemsChanged',
				{
					argument: {
						...argument,
						newValue: compare.newItemsClone,
						oldValue: compare.oldItemsClone
					},
					controlId: this.name,
					name: 'SelectedItemsChanged',
					sender: 'web'
				}
			);

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'selectedItems',
				newValue: this.selectedItems,
				oldValue: oldSelectedItems,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);

			const selectedValuesChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'selectedValues',
				newValue: this.selectedItems.map(i => i.value),
				oldValue: oldSelectedItems.map(i => i.value),
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', selectedValuesChangedArgs);
		}

		const newValue = this.value;
		if (oldValue !== newValue && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: newValue,
				oldValue: oldValue
			};

			this.emit<IPropertyChangedEventArgs<any>>('event:OnValueChanged', argument);
			this.emit<IUserEvent<IPropertyChangedEventArgs<any>>>('event:server:OnValueChanged', {
				argument,
				controlId: this.name,
				name: 'ValueChanged',
				sender: 'web'
			});

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'value',
				newValue: this.value,
				oldValue,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);
		}

		const newText = this.text;
		if (oldText !== newText && this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: newText,
				oldValue: oldText
			};

			this.emit<IPropertyChangedEventArgs<string>>('event:OnTextChanged', argument);
			this.emit<IUserEvent<IPropertyChangedEventArgs<string>>>('event:server:OnTextChanged', {
				argument,
				controlId: this.name,
				name: 'TextChanged',
				sender: 'web'
			});

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'text',
				newValue: this.text,
				oldValue,
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);
		}
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields(properties);
		return [
			...fields,
			{
				name: 'textSeparator',
				type: 'string'
			},
			{
				name: 'valueSeparator',
				type: 'string'
			},
			{
				name: 'showSearch',
				type: 'boolean'
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			textSeparator: this.textSeparator,
			valueSeparator: this.valueSeparator,
			showSearch: this.showSearch
		};
	}

	__render(children?: React.ReactNode): JSX.Element {
		return super.__render(children);
	}
}
